import * as React from 'react';
import type {Key} from '@react-types/shared';
import {useState, useRef} from 'react';
import {useResizeObserver} from '../hooks/useResizeObserver';
import {
  RESERVED_SPACER_SIZE,
  TRUNCATION_WRAPPER_STYLES,
  UNSET_TRUNCATION_WRAPPER_STYLES,
} from './internal';

function isSetDifferent(set1: Set<unknown>, set2: Set<unknown>) {
  if (set1.size !== set2.size) {
    return true;
  }

  return !![...set1].find((x) => !set2.has(x));
}

function getEmptyMeasure() {
  return {
    left: 0,
    right: 0,
    width: 0,
  };
}

function runMeasurements(
  wrapper: HTMLElement,
  menu: HTMLElement | null,
  tabs: Array<HTMLElement>,
) {
  const measurements = new Map<Element, DOMRect>();
  const measure = (node: Element | null) => {
    if (node == null) {
      return getEmptyMeasure();
    }
    const m = measurements.get(node);
    if (m == null) {
      throw new Error('Measurement not found for node');
    }
    return m;
  };

  // Show all the elements so we can measure them
  tabs.forEach((tab) => {
    Object.assign(tab.style, {
      display: '',
      textOverflow: '',
      minWidth: '',
      overflow: '',
      flex: '',
    });
    const truncationWrapper = tab.querySelector<HTMLElement>(
      '[data-truncation-wrapper]',
    );
    if (truncationWrapper) {
      Object.assign(truncationWrapper.style, UNSET_TRUNCATION_WRAPPER_STYLES);
    }
  });

  if (menu) {
    // Reveal the menu for measurement. Inline display overrides any prior
    // inline display:none set by updateOverflow.
    menu.style.display = 'flex';
    // eslint-disable-next-line @sail/ui-no-reflow
    measurements.set(menu, menu.getBoundingClientRect());
  }

  // Measure everything in one pass to avoid layout thrashing
  // eslint-disable-next-line @sail/ui-no-reflow
  measurements.set(wrapper, wrapper.getBoundingClientRect());

  tabs.forEach((tab) => {
    // eslint-disable-next-line @sail/ui-no-reflow
    measurements.set(tab, tab.getBoundingClientRect());
  });

  // @ts-expect-error - Error after enabling noUncheckedIndexedAccess
  const gapSize = measure(tabs[1]).left - measure(tabs[0]).right;
  const contentSize = Math.floor(
    // @ts-expect-error - Error after enabling noUncheckedIndexedAccess
    Math.abs(measure(tabs[tabs.length - 1]).right - measure(tabs[0]).left),
  );
  const totalSize = Math.floor(measure(wrapper).width);

  // Assuming we're collapsing, the total available space should only
  // be the space for tabs, not including the space we require for the
  // menu, reserved space, and gap
  const reservedSpace = Math.min(
    RESERVED_SPACER_SIZE,
    Math.floor(totalSize * 0.4),
  );

  let availableSpace = totalSize;

  availableSpace -= measure(menu).width;
  availableSpace -= reservedSpace;
  availableSpace -= gapSize;

  return {measure, gapSize, contentSize, totalSize, availableSpace};
}

export function useCollapsibleTabs(
  wrapperRef: React.RefObject<HTMLElement>,
  tabsRef: React.RefObject<HTMLElement>,
  menuRef: React.RefObject<HTMLElement>,
  initialSelectedKey?: Key,
) {
  const [collapseState, setCollapseState] = useState({
    menuItems: new Set<string>(),
    hiddenIds: new Set<string>(),
    truncatedId: null as string | null,
  });
  const selectedKeyRef = useRef<Key | undefined>(initialSelectedKey);

  const updateOverflow = React.useCallback(() => {
    const wrapper = wrapperRef.current;
    if (wrapper == null) {
      throw new Error('measurement node not found');
    }

    const tabList = tabsRef.current;
    const tabs: Array<HTMLElement> = [];
    (tabList ? [...tabList.children] : []).forEach((tab) => {
      if ((tab as HTMLElement).dataset.collectionId) {
        tabs.push(tab as HTMLElement);
      }
    });

    const menu = menuRef.current;

    const selectedKey = selectedKeyRef.current;

    // Build up state in these variables
    let elementsToHide: Array<HTMLElement> = [];
    let elementsForMenu: Array<HTMLElement> = [];
    let elementToTruncate: HTMLElement | null = null;

    // Do the measuring. Note that this does DOM manipulation and will
    // force all the elements to be displayed so we can measure them.
    // Later in this function, we will update the DOM based on what we
    // want hidden
    const {measure, gapSize, contentSize, totalSize, availableSpace} =
      runMeasurements(wrapper, menu, tabs);

    if (contentSize > totalSize) {
      // Walk through the tabs backwards, hiding them one by one until
      // the content fits
      let currentSize = contentSize;
      [...tabs].reverse().forEach((tab) => {
        if (currentSize > availableSpace) {
          elementsForMenu.push(tab);
          elementsToHide.push(tab);

          currentSize -= measure(tab).width;
          currentSize -= gapSize;
        }
      });

      elementsForMenu.reverse();
    }

    // Transform the instructions based on some heuristics:
    // * We never want to collapse a single tab into a menu, so in
    //   that case make it truncated instead
    // * Otherwise, if the selected tab is collapsed, we always want
    //   to show it. Force that one to be shown and truncated (this
    //   works because in the layout we reserved extra space for an
    //   additional tab)
    if (elementsForMenu.length === 1) {
      // @ts-expect-error - Error after enabling noUncheckedIndexedAccess
      elementToTruncate = elementsForMenu[0];
      elementsForMenu = [];
      elementsToHide = [];
    } else if (elementsForMenu.length > 1) {
      let unhideElement = elementsToHide.find(
        (c) => (c as HTMLElement).dataset.collectionId === selectedKey,
      );
      if (!unhideElement) {
        // Always show at least one tab in the reserved space. If the
        // selected tab isn't hidden, pull out the first item in the menu
        unhideElement = elementsForMenu[0];
        elementsForMenu = elementsForMenu.slice(1);
      }

      if (unhideElement) {
        elementToTruncate = unhideElement;
        elementsToHide = elementsToHide.filter((c) => c !== unhideElement);
      }
    }

    // To visualize measurements, add this import statement:
    // import {debugMeasurements} from './debug';
    //
    // And uncomment this call:
    //
    // debugMeasurements({
    //   measure,
    //   wrapper,
    //   menu,
    //   tabs,
    //   gapSize,
    //   elementsToHide,
    //   elementsForMenu,
    //   elementToTruncate,
    // });

    // Execute the instructions. This involves direct DOM manipulation
    // to hide and show things. React owns the truncation wrapper itself.
    if (menu) {
      if (elementsForMenu.length === 0) {
        menu.style.display = 'none';
      } else {
        menu.style.display = 'flex';
      }
    }

    elementsToHide.forEach((el) => {
      el.style.display = 'none';
    });

    if (elementToTruncate) {
      elementToTruncate.style.flex = '1 1 0';
      elementToTruncate.style.minWidth = '0';
      // Set title attribute so users can see full tab name on hover
      elementToTruncate.title = elementToTruncate.textContent || '';
      // If this tab was already truncated, its React-owned wrapper exists.
      // Reapply the styles cleared for measurement; newly truncated tabs get
      // these styles when React renders their wrapper.
      const truncationWrapper = elementToTruncate.querySelector<HTMLElement>(
        '[data-truncation-wrapper]',
      );
      if (truncationWrapper) {
        Object.assign(truncationWrapper.style, TRUNCATION_WRAPPER_STYLES);
      }
    }

    // Set the new state in React. We need to do this because things
    // like keyboard navigation are based on the collection, so we
    // need to reflect these updates there
    setCollapseState((prev: typeof collapseState) => {
      const state = {
        menuItems: new Set(elementsForMenu.map((c) => c.dataset.collectionId!)),
        hiddenIds: new Set(elementsToHide.map((c) => c.dataset.collectionId!)),
        truncatedId: elementToTruncate?.dataset.collectionId ?? null,
      };

      if (
        isSetDifferent(prev.menuItems, state.menuItems) ||
        isSetDifferent(prev.hiddenIds, state.hiddenIds) ||
        prev.truncatedId !== state.truncatedId
      ) {
        return state;
      }
      return prev;
    });
  }, [menuRef, tabsRef, wrapperRef]);

  const setSelected = React.useCallback((key: Key) => {
    selectedKeyRef.current = key;
  }, []);

  useResizeObserver({
    node: wrapperRef,
    handler: updateOverflow,
  });

  return {
    hiddenIds: collapseState.hiddenIds,
    menuItems: collapseState.menuItems,
    truncatedId: collapseState.truncatedId,
    updateOverflow,
    setSelected,
  };
}
